import Link from "@mui/material/Link";
import { Link as RouterLink } from "react-router-dom";
import Chip from "@mui/material/Chip";
import { capitalizeWords, formatBrDate } from "../../../utilities/Utilities";
import { translateDbLabel } from "../../../utilities/translateDbLabel";
import MessageLogCell from "./MessageLogCell";

const ColumnsNfe = (t, isEnglish) => [
  {
    field: "accesskey",
    headerName: t("columns.accessKey"),
    width: 370,
    renderCell: (params) => (
      <Link component={RouterLink} to={`/inbound/historic/${params.value}`}>
        {params.value}
      </Link>
    ),
  },
  {
    field: "message_log",
    headerName: t("columns.messages"), // adicione a tradução
    width: 80,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <MessageLogCell messages={params.row?.message_log ?? []} />
    ),
  },  
  { field: "company_code", headerName: t("columns.company"), width: 90 },
  { field: "plant", headerName: t("columns.plant"), width: 90 },
  {
    field: "supplier_cnpj",
    headerName: t("columns.supplierCnpj"),
    width: 170,
    renderCell: (params) => {
      const value = params.row?.supplier_cnpj;
      if (!value) return "";

      const digits = String(value).replace(/\D/g, "");

      if (digits.length !== 14) return value;

      return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5",
      );
    },
  },
  {
    field: "supplier_cpf",
    headerName: t("columns.supplierCpf"),
    width: 150,
    renderCell: (params) => {
      const value = params.row?.supplier_cpf;
      if (!value) return "";

      const digits = String(value).replace(/\D/g, "");

      if (digits.length !== 11) return value;

      return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
    },
  },
  {
    field: "name_supplier_",
    headerName: t("columns.supplierName"),
    width: 120,
    renderCell: (params) => capitalizeWords(params.row?.name_supplier_),
  },
    {
    field: "recipient_cnpj",
    headerName: t("columns.destCnpj"),
    width: 170,
    renderCell: (params) => {
      const value = params.row?.recipient_cnpj;
      if (!value) return "";

      const digits = String(value).replace(/\D/g, "");

      if (digits.length !== 14) return value;

      return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        "$1.$2.$3/$4-$5",
      );
    },
  },
  {
    field: "recipient_name",
    headerName: t("columns.destName"),
    width: 120,
    renderCell: (params) => capitalizeWords(params.row?.recipient_name),
  },
  { field: "cfop", headerName: t("columns.cfop"), width: 90 },
  {
    field: "process_description",
    headerName: t("columns.process"),
    width: 120,
    renderCell: (params) => capitalizeWords(translateDbLabel(params.row?.process_description, isEnglish)),
  },
  {
    field: "step_description",
    headerName: t("columns.step"),
    width: 140,
    renderCell: (params) => {
      const label = capitalizeWords(translateDbLabel(params.row?.step_description, isEnglish));
      if (!label) return "";
      return (
        <Chip
          label={label}
          size="small"
          variant="outlined"
          sx={{ borderRadius: "6px", fontSize: "0.65rem" }}
        />
      );
    },
  },
  { field: "version_xml", headerName: t("columns.xmlVersion"), width: 110 },
  { field: "number_nfe", headerName: t("columns.nfeNumber"), width: 90 },
  { field: "serie", headerName: t("columns.series"), width: 80 },
  {
    field: "issue_date",
    headerName: t("columns.issueDate"),
    width: 130,
    renderCell: (params) => {
      const value = params.row?.issue_date;
      if (!value) return "";
      return formatBrDate(value);
    },
  },
  {
    field: "issue_time",
    headerName: t("columns.issueTime"),
    width: 115,
    renderCell: (params) => {
      const value = params.row?.issue_time;
      if (!value) return "";

      return value.substring(11, 19); // HH:mm:ss
    },
  },
  {
    field: "received_date",
    headerName: t("columns.createdDate"),
    width: 130,
    renderCell: (params) => {
      const value = params.row?.received_date;
      if (!value) return "";
      return formatBrDate(value);
    },
  },
  {
    field: "received_time",
    headerName: t("columns.createdTime"),
    width: 115,
    renderCell: (params) => {
      const value = params.row?.received_time;
      if (!value) return "";

      return value.substring(11, 19); // HH:mm:ss
    },
  },
  { field: "log_sefaz", headerName: t("columns.sefazLog"), width: 320 },
  { field: "status_sefaz", headerName: t("columns.sefazStatus"), width: 120 },
  {
    field: "execution_status",
    headerName: t("columns.processStatus"),
    width: 140,
    renderCell: (params) => {
      const status = String(params.value || "").toUpperCase();

      if (status === "S") {
        return (
          <Chip
            label={t("status.success")}
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: "#DCFCE7",
              color: "#166534",
              borderRadius: "6px",
            }}
          />
        );
      }

      if (status === "E") {
        return (
          <Chip
            label={t("status.error")}
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: "#FEE2E2",
              color: "#991B1B",
              borderRadius: "6px",
            }}
          />
        );
      }

      if (status === "W") {
        return (
          <Chip
            label={t("status.warning")}
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: "#FEF9C3",
              color: "#854D0E",
              borderRadius: "6px",
            }}
          />
        );
      }


      return <span>{params.value || "—"}</span>;
    },
  },
  { field: "rejection_code", headerName: t("columns.rejectCode"), width: 110 },
  { field: "origin_state", headerName: t("columns.ufOrigin"), width: 105 },
  { field: "destination_state", headerName: t("columns.ufDest"), width: 105 },
  {
    field: "total_value",
    headerName: t("columns.totalValue"),
    width: 140,
    renderCell: (params) => {
      const value = Number(params.row?.total_value ?? 0);
      return value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
  },
  { field: "event_sefaz", headerName: t("columns.sefazEvent"), width: 150 },
];

export default ColumnsNfe;